import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { defer, fromEvent, Observable, of, throwError, timer } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, map, startWith, switchMap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { StreamingMaxHeight } from './streaming-playback-policy';
import { streamingApiUrl } from './streaming-config';

export interface ReunionMeeting {
  id: string;
  label: string;
  reunionNumber: number | null;
  date: string;
  trackName: string;
  channelId: string | null;
  phase: 'live' | 'onair' | 'upcoming' | 'ended' | 'unknown';
  /** True only when every reported race has an explicit finished status. */
  finished?: boolean;
  /** Catalogue eligibility is separate from provider signal availability. */
  catalogueEligible?: boolean;
  catalogueAvailableFrom?: string | null;
  catalogueValidUntil?: string | null;
  catalogueRace?: ReunionMeeting['race'];
  playable: boolean;
  unavailableReason: string | null;
  startsAt: string | null;
  endsAt: string | null;
  race: {
    number: number | null;
    index: number;
    name: string;
    startsAt: string | null;
    status: 'live' | 'upcoming' | 'finished' | 'unknown';
    estimated: boolean;
    quinte: boolean;
  } | null;
  signalOwner: { id: string; label: string } | null;
}

export interface ReunionGuide {
  enabled: boolean;
  loading: boolean;
  stale: boolean;
  updatedAt: string | null;
  serverTime: string;
  refreshAfterSeconds: number;
  meetings: ReunionMeeting[];
  channels: { id: string; label: string; type: string; playable: boolean }[];
  recommendedId: string | null;
}

export interface ReunionSession {
  sessionId: string;
  url: string;
  expiresAt: string;
  renewAfterSeconds: number;
  selectionId: string;
  channelId: string;
  maxHeight: StreamingMaxHeight;
}

export interface ReunionGuideUpdate {
  guide?: ReunionGuide;
  failed?: boolean;
}

export function isReunionGuide(value: unknown): value is ReunionGuide {
  return !!value && typeof value === 'object' && Array.isArray((value as ReunionGuide).meetings);
}

export function streamingRoleAllowed(role: string): boolean {
  // Keep aligned with the backend control guard, AuthGuard('newClientUser'): every
  // known role. The clients a branch creates carry newClientUser until they set
  // their own password; clientRegistered means "may self-deposit", not "is a player".
  return ['newClientUser', 'clientRegistered', 'clientUser', 'branchAdmin', 'superAdmin', 'goatAdmin'].indexOf(role) !== -1;
}

@Injectable({ providedIn: 'root' })
export class ReunionStreamingService {
  // One gate shared with TokenInterceptor (streaming-config.ts).
  private readonly apiUrl = streamingApiUrl(environment);
  readonly enabled = this.apiUrl !== '';

  constructor(private http: HttpClient, @Inject(DOCUMENT) private document: Document) {}

  /**
   * Watched by any viewer who may see named meetings, panel open or closed (the closed
   * bar's live dot reads it); background tabs make no guide requests.
   */
  watchGuide(): Observable<ReunionGuideUpdate> {
    if (!this.enabled) {
      return of({ guide: undefined });
    }
    return defer(() => fromEvent(this.document, 'visibilitychange').pipe(
      startWith(null),
      map(() => this.document.visibilityState !== 'hidden'),
      distinctUntilChanged(),
      switchMap((visible) => visible ? timer(0, 45000).pipe(
        exhaustMap(() => this.http.get<ReunionGuide>(`${this.apiUrl}/guide`).pipe(
          timeout(12000),
          // A 200 that is not a guide (an empty body is `null`; test-be answers an empty 200
          // on any path without a route) is a failure, never "no guide yet": the player would
          // otherwise keep its spinner forever.
          map((guide) => isReunionGuide(guide) ? { guide } : { failed: true }),
          catchError(() => of({ failed: true }))
        ))
      ) : of({}))
    ));
  }

  createSession(selectionId: string, maxHeight: StreamingMaxHeight = 480): Observable<ReunionSession> {
    return this.sessionRequest('/sessions', { selectionId, maxHeight });
  }

  publicPmuUrl(sourceUrl: string, maxHeight: StreamingMaxHeight): string | null {
    return this.enabled && sourceUrl === 'https://genybet-tvcp1-p28-cdn.hexaglobe.net/slot03/llhls/playlist.m3u8'
      ? `${this.apiUrl}/public/pmu/master.m3u8?maxHeight=${maxHeight}` : null;
  }

  renewSession(sessionId: string): Observable<ReunionSession> {
    return this.sessionRequest('/sessions/renew', { sessionId });
  }

  revokeSession(sessionId: string): Observable<unknown> {
    if (!this.enabled) {
      return of(null);
    }
    return this.http.post(`${this.apiUrl}/media/${encodeURIComponent(sessionId)}/revoke`, {}).pipe(
      timeout(4000), catchError(() => of(null))
    );
  }

  private sessionRequest(path: string, body: object): Observable<ReunionSession> {
    if (!this.enabled) {
      return throwError({ error: { error: { code: 'STREAMING_DISABLED' } } });
    }
    return this.http.post<ReunionSession>(`${this.apiUrl}${path}`, body).pipe(
      timeout(12000),
      map((session) => {
        // The video element never receives Firebase credentials. Only a URL on
        // our configured gateway may receive this short-lived media capability.
        const api = new URL(this.apiUrl);
        const media = new URL(session.url);
        if (!session.sessionId || (session.maxHeight !== 480 && session.maxHeight !== 720) ||
          !Number.isFinite(Date.parse(session.expiresAt)) ||
          !Number.isFinite(session.renewAfterSeconds) || session.renewAfterSeconds <= 0 ||
          media.origin !== api.origin || media.username || media.password ||
          !media.pathname.startsWith(`${api.pathname.replace(/\/$/, '')}/media/`)) {
          throw new Error('Invalid streaming session response');
        }
        return session;
      })
    );
  }
}
